import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { subscribeEmail } from "@/lib/marketing/subscribers.functions";
import { ArrowRight, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

type Variant = "inline" | "stacked" | "compact";

export function JoinList({
  source,
  variant = "inline",
  heading,
  subheading,
  cta = "Join the list",
  className = "",
}: {
  source: string;
  variant?: Variant;
  heading?: string;
  subheading?: string;
  cta?: string;
  className?: string;
}) {
  const subscribe = useServerFn(subscribeEmail);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await subscribe({
        data: {
          email,
          source,
          user_agent:
            typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 480) : null,
        },
      });
      setDone(true);
      setEmail("");
    } catch (err: any) {
      toast.error(err?.message || "Could not subscribe");
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div
        className={`inline-flex items-center gap-2 rounded-md border border-success/40 bg-success/10 px-4 py-3 text-sm text-success ${className}`}
      >
        <CheckCircle2 className="h-4 w-4" />
        You're on the list — we'll be in touch.
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <form onSubmit={onSubmit} className={`flex w-full max-w-md gap-2 ${className}`}>
        <input
          type="email"
          required
          maxLength={255}
          placeholder="you@agency.gov"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 min-w-0 rounded-md border border-panel-border bg-background/60 px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-cyan"
        />
        <button
          type="submit"
          disabled={busy}
          className="inline-flex shrink-0 items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 ring-glow disabled:opacity-60"
        >
          {busy ? "…" : cta}
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </form>
    );
  }

  return (
    <div className={`${variant === "stacked" ? "" : "glass-strong rounded-xl p-6"} ${className}`}>
      {heading && (
        <div className="mb-3">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">
            <Mail className="h-3 w-3" /> Newsletter · early access
          </div>
          <h3 className="mt-2 font-display text-2xl sm:text-3xl">{heading}</h3>
          {subheading && (
            <p className="mt-2 text-sm text-muted-foreground max-w-xl">{subheading}</p>
          )}
        </div>
      )}
      <form
        onSubmit={onSubmit}
        className="flex w-full flex-col gap-2 sm:flex-row sm:max-w-xl"
      >
        <input
          type="email"
          required
          maxLength={255}
          placeholder="you@agency.gov"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="flex-1 min-w-0 rounded-md border border-panel-border bg-background/60 px-4 py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-cyan"
        />
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center justify-center gap-1.5 rounded-md bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:opacity-90 ring-glow disabled:opacity-60 whitespace-nowrap"
        >
          {busy ? "Subscribing…" : cta}
          <ArrowRight className="h-4 w-4" />
        </button>
      </form>
      <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
        No spam. Product updates only, and you can leave any time.
      </p>
    </div>
  );
}